import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/landing.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShieldLogo = ({ stroke = '#B76E79', fill = 'rgba(183,110,121,0.08)' }) => (
  <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <path d="M32 6 L54 14 L54 30 C54 42 46 52 32 56 C18 52 10 42 10 30 L10 14 Z" stroke={stroke} strokeWidth="3" fill={fill} />
  </svg>
);

const Feature = ({ icon, title, body }) => (
  <div className="feature">
    <div className="icon">{icon}</div>
    <h3>{title}</h3>
    <p>{body}</p>
  </div>
);

const Step = ({ title, body }) => (
  <div className="step">
    <h3>{title}</h3>
    <p>{body}</p>
  </div>
);

const Faq = ({ q, children }) => (
  <details className="faq">
    <summary>{q}</summary>
    <p>{children}</p>
  </details>
);

// Detect platform so we can show the right store badge to each visitor.
// • iOS / iPadOS → Apple App Store badge only (Apple guideline 2.3.10)
// • Android      → Google Play badge only
// • Desktop / other → ALL badges (best for sharing with family on other platforms)
function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios'; // iPadOS 13+
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/selah-slate/id6779376823';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.selahandstone.selahslate';
const APPLE_BADGE_SRC = 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83';
const PLAY_BADGE_SRC = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';
const MS_STORE_URL = 'https://apps.microsoft.com/detail/9PJF6L5GH0TF';
const MS_BADGE_SRC = 'https://get.microsoft.com/images/en-us%20dark.svg';

// "Be first to know" email capture for upcoming brands.
function WaitlistForm({ brand, slug }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(() => {
    try { return localStorage.getItem(`waitlist_${slug}`) === '1' ? 'done' : 'idle'; } catch (e) { return 'idle'; }
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await axios.post(`${API_URL}/api/brand-waitlist`, { email: email.trim(), brand });
      try { localStorage.setItem(`waitlist_${slug}`, '1'); } catch (e2) { /* private mode */ }
      setStatus('done');
    } catch (err) {
      setStatus('error');
    }
  };
  if (status === 'done') {
    return (
      <p data-testid={`waitlist-success-${slug}`} style={{ fontSize: '0.82rem', color: '#B76E79', fontWeight: 600, margin: '14px 0 0' }}>
        ✓ You're on the list — we'll email you at launch.
      </p>
    );
  }
  return (
    <form onSubmit={submit} data-testid={`waitlist-form-${slug}`} style={{ marginTop: 14 }}>
      <p style={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(54,69,79,0.45)', fontWeight: 700, margin: '0 0 8px' }}>
        Be first to know
      </p>
      <div style={{ display: 'flex', gap: 6, maxWidth: 280, margin: '0 auto' }}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid={`waitlist-input-${slug}`}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 12px',
            fontSize: '0.85rem',
            border: '1px solid rgba(54,69,79,0.2)',
            borderRadius: 6,
            outline: 'none',
            fontFamily: 'inherit',
            color: '#36454F',
          }}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          data-testid={`waitlist-submit-${slug}`}
          style={{
            background: '#B76E79',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 14px',
            fontSize: '0.82rem',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            opacity: status === 'loading' ? 0.7 : 1,
          }}
        >
          {status === 'loading' ? '…' : 'Notify me'}
        </button>
      </div>
      {status === 'error' && (
        <p data-testid={`waitlist-error-${slug}`} style={{ fontSize: '0.78rem', color: '#B00020', margin: '8px 0 0' }}>
          Something went wrong — please try again.
        </p>
      )}
    </form>
  );
}

// Dismissible "now on all 3 stores" bar. Platform-aware: never shows
// Google Play / Microsoft links to iOS visitors (Apple guideline 2.3.10).
function AnnouncementBar({ platform }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('announce_3stores_dismissed') === '1'; } catch (e) { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem('announce_3stores_dismissed', '1'); } catch (e) { /* private mode */ }
    setDismissed(true);
  };
  const links = [];
  if (platform === 'ios' || platform === 'desktop') links.push({ label: 'App Store', href: APP_STORE_URL, testid: 'announcement-apple-link' });
  if (platform === 'android' || platform === 'desktop') links.push({ label: 'Google Play', href: PLAY_STORE_URL, testid: 'announcement-google-link' });
  if (platform === 'desktop') links.push({ label: 'Microsoft Store', href: MS_STORE_URL, testid: 'announcement-microsoft-link' });
  return (
    <div
      data-testid="announcement-bar"
      style={{
        background: '#B76E79',
        color: '#fff',
        padding: '9px 44px 9px 16px',
        textAlign: 'center',
        fontSize: 14,
        fontFamily: 'Arial, sans-serif',
        letterSpacing: 0.2,
        position: 'relative',
        zIndex: 60,
      }}
    >
      <span>
        🎉 Selah Slate is now on{' '}
        {links.map((l, i) => (
          <span key={l.label}>
            {i > 0 && (i === links.length - 1 ? ' & ' : ', ')}
            <a
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={l.testid}
              style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              {l.label}
            </a>
          </span>
        ))}
        {' '}— free, forever.
      </span>
      <button
        onClick={dismiss}
        aria-label="Dismiss announcement"
        data-testid="announcement-bar-dismiss"
        style={{
          position: 'absolute',
          right: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.85)',
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 6,
        }}
      >
        ✕
      </button>
    </div>
  );
}

export default function LandingPage() {
  const year = new Date().getFullYear();
  const platform = detectPlatform();
  const showApple = platform === 'ios' || platform === 'desktop';
  const showAndroid = platform === 'android' || platform === 'desktop';
  const showWindows = platform === 'desktop';

  return (
    <div className="selah-landing" data-testid="landing-page">
      <AnnouncementBar platform={platform} />
      {/* HEADER */}
      <header className="site">
        <div className="container inner">
          <a href="#top" className="brand" aria-label="Selah Slate home">
            <ShieldLogo />
            <span className="name">Selah Slate&trade;</span>
          </a>
          <nav>
            <Link className="btn btn-primary" to="/login" data-testid="header-open-app">Open the app →</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <div id="top" className="hero">
        <div className="container hero-grid">
          <div className="fade-in">
            <div className="tag">Faith-based · Free forever · Ad-free</div>
            <h1>Clear your credit.<br />Reclaim your legacy.</h1>
            <p className="lede">
              Selah Slate is a free, faith-based companion helping you draft FCRA credit-dispute letters, track bureau responses, sync a shared household budget in real time, and rebuild with scripture-grounded encouragement along the way.
            </p>
            <div className="hero-ctas">
              <Link className="btn btn-primary" to="/register" data-testid="hero-cta-register">Create your free account →</Link>
              <a className="btn btn-ghost" href="#how">See how it works</a>
            </div>
            {(showApple || showAndroid) && (
              <div
                style={{
                  marginTop: 18,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                }}
                data-testid="hero-store-badges"
              >
                {showApple && (
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                    data-testid="hero-apple-badge"
                    style={{ display: 'inline-block', transition: 'transform 0.18s ease, opacity 0.18s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.opacity = '0.92'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                  >
                    <img
                      src={APPLE_BADGE_SRC}
                      alt="Download on the App Store"
                      style={{ height: 56, width: 'auto', display: 'block' }}
                    />
                  </a>
                )}
                {showAndroid && (
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it on Google Play"
                    data-testid="hero-google-play-badge"
                    style={{ display: 'inline-block', transition: 'transform 0.18s ease, opacity 0.18s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.opacity = '0.92'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                  >
                    <img
                      src={PLAY_BADGE_SRC}
                      alt="Get it on Google Play"
                      style={{ height: 56, width: 'auto', display: 'block' }}
                    />
                  </a>
                )}
                {showWindows && (
                  <a
                    href={MS_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it from Microsoft Store"
                    data-testid="hero-microsoft-badge"
                    style={{ display: 'inline-block', transition: 'transform 0.18s ease, opacity 0.18s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.opacity = '0.92'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                  >
                    <img
                      src={MS_BADGE_SRC}
                      alt="Get it from Microsoft Store"
                      style={{ height: 56, width: 'auto', display: 'block' }}
                    />
                  </a>
                )}
              </div>
            )}
            <div className="hero-note">
              <span>✓ No fees. Ever.</span>
              <span>✓ No ads. No selling your data.</span>
              <span>✓ Delete your account anytime.</span>
            </div>
          </div>

          <div className="hero-card fade-in delay-2" aria-hidden="true">
            <span className="badge">A glimpse of the app</span>
            <img
              src="/landing/dashboard.jpg"
              alt="Selah Slate dashboard preview"
              style={{ width: '100%', height: 'auto', borderRadius: 6, display: 'block', margin: '6px 0 16px', boxShadow: '0 18px 48px rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.08)' }}
            />
            <div className="verse">
              "I am doing a new thing; now it springs forth — do you not perceive it?"
              <span>— Isaiah 43:19</span>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: FAMILY BUDGET ANNOUNCEMENT */}
      <section
        style={{
          padding: '48px 0',
          background: 'linear-gradient(180deg, #F5F5F5 0%, #EDE4DF 100%)',
          borderTop: '1px solid rgba(54,69,79,0.06)',
          borderBottom: '1px solid rgba(54,69,79,0.06)',
        }}
        data-testid="family-budget-announcement"
      >
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: 40,
              alignItems: 'center',
            }}
            className="fb-announcement-grid"
          >
            <div>
              <div
                style={{
                  display: 'inline-block',
                  padding: '5px 12px',
                  background: '#B76E79',
                  color: '#fff',
                  fontFamily: 'Manrope',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '1.4px',
                  textTransform: 'uppercase',
                  borderRadius: 3,
                  marginBottom: 16,
                }}
              >
                New in Selah Slate
              </div>
              <h2
                style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: 'clamp(30px, 4vw, 44px)',
                  color: '#36454F',
                  margin: '0 0 16px',
                  lineHeight: 1.15,
                }}
              >
                Family Budget — plan together, live.
              </h2>
              <p
                style={{
                  fontFamily: 'Manrope',
                  color: 'var(--text-soft)',
                  fontSize: 17,
                  lineHeight: 1.6,
                  marginBottom: 22,
                }}
              >
                Envelopes for every dollar. A Giving Impact tracker paired with scripture. And the moment someone in your household logs an expense, everyone sees it. Free forever. Ad-free. Grounded in the Word.
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 26px',
                  display: 'grid',
                  gap: 10,
                  fontFamily: 'Manrope',
                  color: '#36454F',
                }}
              >
                <li>✝️&nbsp;&nbsp;<strong>Tithe &amp; Giving</strong> envelope with an annual Impact tracker</li>
                <li>👀&nbsp;&nbsp;<strong>Live sync</strong> — no more "who spent what?" texts, for anyone you share money with</li>
                <li>📖&nbsp;&nbsp;A verse on every screen, from Proverbs 21:5 to 11:25</li>
                <li>🆓&nbsp;&nbsp;Free forever. No ads. No data sold.</li>
              </ul>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <Link
                  className="btn btn-primary"
                  to="/register"
                  data-testid="family-budget-cta-register"
                >
                  Try Family Budget free →
                </Link>
                <Link
                  className="btn btn-ghost"
                  to="/login"
                  data-testid="family-budget-cta-login"
                >
                  Already signed up? Open the app
                </Link>
              </div>
              <p
                style={{
                  marginTop: 18,
                  fontFamily: 'Manrope',
                  fontStyle: 'italic',
                  fontSize: 13,
                  color: 'var(--text-soft)',
                }}
              >
                Built from your feedback — shipped now, not "soon."
              </p>
            </div>

            <div
              style={{
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 20px 48px rgba(54,69,79,0.18)',
                border: '1px solid rgba(54,69,79,0.08)',
                background: '#fff',
              }}
            >
              <img
                src="/landing/family-budget/hero.png"
                alt="Selah Slate Family Budget on two phones — live sync across your household"
                style={{ width: '100%', height: 'auto', display: 'block' }}
                loading="lazy"
                data-testid="family-budget-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR — inclusive audience grid */}
      <section
        style={{ padding: '48px 0', background: '#fff', borderTop: '1px solid rgba(54,69,79,0.06)' }}
        data-testid="who-its-for-section"
      >
        <div className="container">
          <div className="section-head" style={{ marginBottom: 28 }}>
            <div className="eyebrow">Who it's for</div>
            <h2>For every kind of household.</h2>
            <p>
              Selah Slate isn't just for couples, and it isn't just for people fixing their credit.
              Use the <strong>credit dispute tools</strong>, the <strong>Family Budget</strong>, or both — they work independently.
              Clear your slate wherever it needs clearing.
            </p>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 16,
              maxWidth: 1080,
              margin: '0 auto',
            }}
            data-testid="audience-grid"
          >
            {[
              { icon: '🧍', label: 'Single', copy: 'Steward your money and your credit on your own.' },
              { icon: '💛', label: 'Married', copy: 'Live-sync the household budget with your partner.' },
              { icon: '👩‍👧', label: 'Single parents', copy: 'Track every dollar, and every dispute.' },
              { icon: '🏠', label: 'Roommates', copy: 'Share bills without the "who paid?" texts.' },
              { icon: '👵', label: 'Multi-generational', copy: 'Steward together across three generations.' },
              { icon: '⛪', label: 'Ministry teams', copy: 'Manage benevolence funds and shared budgets.' },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: '20px 16px',
                  background: '#F5F5F5',
                  border: '1px solid rgba(54,69,79,0.08)',
                  borderRadius: 6,
                  textAlign: 'center',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = 'rgba(183,110,121,0.35)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(54,69,79,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(54,69,79,0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`audience-tile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div style={{ fontSize: 32, marginBottom: 8, lineHeight: 1 }} aria-hidden="true">{item.icon}</div>
                <div
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#36454F',
                    marginBottom: 4,
                  }}
                >
                  {item.label}
                </div>
                <div
                  style={{
                    fontFamily: 'Manrope',
                    fontSize: 12,
                    color: 'var(--text-soft)',
                    lineHeight: 1.5,
                  }}
                >
                  {item.copy}
                </div>
              </div>
            ))}
          </div>
          <p
            style={{
              marginTop: 24,
              textAlign: 'center',
              fontFamily: 'Manrope',
              fontSize: 13,
              fontStyle: 'italic',
              color: 'var(--text-soft)',
              maxWidth: 620,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            The credit dispute tools and the Family Budget can be used together — or entirely on their own. Pick the tool for the season you're in.
          </p>
        </div>
      </section>

      {/* WHY WE BUILT THIS */}
      <section>
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Our why</div>
            <h2>Why we built Selah Slate.</h2>
            <p>
              This isn't another credit-repair startup. It's a mission — built around one conviction:{' '}
              <em>no one should have to pay to fix errors on their own credit report.</em>
            </p>
          </div>

          <div className="why-grid">
            <div className="why-card">
              <h3>We see families being taken advantage of.</h3>
              <p>Credit-repair companies charge $75–$150 a month to send letters any consumer can send for free under the Fair Credit Reporting Act. Many of the families who need help the most are the ones paying the most — often for months, for outcomes that are not guaranteed.</p>
            </div>
            <div className="why-card">
              <h3>We believe stewardship is a spiritual act.</h3>
              <p>Scripture has a lot to say about money, debt, and legacy — and almost none of it involves fear. We want a tool that pairs practical next steps with the encouragement of a faith community: devotions, a journal, a testimony wall, and a reminder that God is doing a new thing, even in the financial realm.</p>
            </div>
            <div className="why-card">
              <h3>We want to leave a legacy, not a subscription.</h3>
              <p>Selah and Stone Legacy Holdings exists to build free resources that outlive us. Selah Slate has no paywall, no ads, and no data brokering — and it never will. If it helps one family break a financial "curse," it's done its job.</p>
            </div>
          </div>

          <div className="why-verse">
            <p>
              "The rich rule over the poor, and the borrower is slave to the lender."
              <span>— Proverbs 22:7</span>
            </p>
            <p style={{ marginTop: 18, color: 'var(--text-soft)', fontStyle: 'italic', fontFamily: 'Manrope' }}>
              Our prayer is simple: that this tool helps you walk out of slavery, one dispute letter at a time.
            </p>
          </div>
        </div>
      </section>

      {/* DASHBOARD PREVIEW */}
      <section className="alt" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="container">
          <div className="section-head" style={{ marginBottom: 32 }}>
            <div className="eyebrow">Built for clarity</div>
            <h2>A calm, focused dashboard.</h2>
            <p>No flashing banners. No upsells. Just your disputes, your progress, and a little encouragement.</p>
          </div>
          <div className="dashboard-frame">
            <div className="dashboard-frame-bar">
              <span></span><span></span><span></span>
              <em>app.selahslate.com/dashboard</em>
            </div>
            <img src="/landing/dashboard.jpg" alt="Selah Slate dashboard" />
          </div>
        </div>
      </section>

      {/* FREE FOREVER PROMISE */}
      <section style={{ padding: '64px 0 40px' }}>
        <div className="container">
          <div className="free-promise">
            <div className="free-promise-badge">The Promise</div>
            <h2 style={{ marginBottom: 14 }}>Why Selah Slate is free — forever.</h2>
            <p>
              The Fair Credit Reporting Act gives <em>every</em> consumer the right to dispute inaccurate information directly with the bureaus, at no cost. Yet credit-repair companies charge families <strong>$75–$150 a month</strong> to do exactly that — often for months, with no guarantee of results.
            </p>
            <p>
              Selah Slate is our answer. It's funded as a legacy initiative of <strong>Selah and Stone Legacy Holdings LLC</strong> — not a startup, not a venture-backed product, not a ministry fundraiser. There are no ads, no premium tiers, no data sales, and no investor pressure to ever add any of those things.
            </p>
            <p style={{ marginBottom: 0 }}>
              If Selah Slate helps one family stop paying a monthly fee for something that should be free — and gives them scripture-grounded courage while they do it — it's done its job. That's why it's free. That's why it will stay free.
            </p>
          </div>
        </div>
      </section>

      {/* IS / IS NOT — click to expand */}
      <section>
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">A word about what we are</div>
            <h2>Clarity before we start.</h2>
            <p>Selah Slate is intentionally simple, and intentionally <em>for everyone</em> — single, married, roommates, single parents, multi-generational homes, ministry teams. It's what credit-repair companies <em>could</em> be if the goal were really helping people — and nothing else. <strong>It's a tool that helps you start, walks with you in between, and helps you finish. It never does the work for you — and no one at Selah Slate ever sees your personal information.</strong></p>
          </div>
          <details className="isnt-details" data-testid="isnt-details">
            <summary className="isnt-summary" data-testid="isnt-details-summary">
              <span>See what the app <strong>is</strong> and <strong>is not</strong></span>
              <span className="isnt-chev" aria-hidden="true">›</span>
            </summary>
            <div className="isnt-grid" style={{ marginTop: '1.5rem' }}>
              <div className="isnt-card is">
                <h3>✓ What the app is</h3>
                <ul>
                  <li>An all-in-one educational companion — <strong>the app</strong> analyzes your credit report, drafts FCRA dispute letters, tracks bureau responses, syncs a shared household budget in real time, guides CFPB escalations, plans debt payoff, and grounds the journey in scripture.</li>
                  <li>A 35-day tracker with smart next-step guidance (MOV letters, CFPB, more).</li>
                  <li>Faith-based encouragement paired with practical steps — devotions, journal, testimony wall.</li>
                  <li>Built for every household — married couples, single parents, adult children caring for parents, roommates sharing bills, or ministry teams stewarding shared funds.</li>
                  <li>Free, private, and ad-free — always. <strong>Your report, letters, and personal information stay on your device and in your account — no human at Selah Slate reviews them.</strong></li>
                </ul>
              </div>
              <div className="isnt-card isnt">
                <h3>✕ What the app is not — and what it does not do</h3>
                <ul>
                  <li>Not a credit repair company, law firm, or financial advisor.</li>
                  <li>Not a guarantee of any specific credit-score outcome.</li>
                  <li>Not a data broker — your data is never sold, and no one at Selah Slate sees your personal information.</li>
                  <li>Not loaded with upsells, ads, or hidden fees.</li>
                  <li>The app does not mail letters for you, negotiate with creditors, or contact bureaus on your behalf — it's a tool that helps you start, walks with you in between, and helps you finish. <strong>You stay in control of every step.</strong></li>
                </ul>
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* FEATURES */}
      <section className="alt">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What's inside</div>
            <h2>Everything you need, nothing you don't.</h2>
            <p>Upload once. The app does the heavy lifting. You stay in the driver's seat every step of the way — no one at Selah Slate ever sees your personal information.</p>
          </div>
          <div className="features">
            <Feature
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>}
              title="AI report analysis"
              body="Upload your Equifax, Experian, or TransUnion report. We pull every tradeline and flag the ones that hurt your score."
            />
            <Feature
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 4h16c1.1 0 2 .9 2 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>}
              title="FCRA dispute letters"
              body="One-click generation of FCRA Section 611 dispute letters. Mail them yourself, at your pace, to all three bureaus."
            />
            <Feature
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>}
              title="35-day response tracker"
              body="We count the days, nudge you before the deadline, and escalate to CFPB or a Method-of-Verification letter if needed."
            />
            <Feature
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.8 18.4A2 2 0 0119 20H5a2 2 0 01-1.8-1.6l-1-7A2 2 0 014 9h16a2 2 0 012 2.4z"/><path d="M16 10V6a4 4 0 00-8 0v4"/></svg>}
              title="Private by design"
              body="Your SSN digits are encrypted. Uploaded PDFs are wiped from our servers within minutes of analysis."
            />
            <Feature
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>}
              title="Scripture-grounded"
              body="Daily encouragement, a progress journal, and a testimony wall. Credit repair rooted in hope, not in fear."
            />
            <Feature
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 3L2 12h3v8h14v-8h3L12 3z"/></svg>}
              title="Debt-freedom calculator"
              body="Auto-populated from your report. See a month-by-month path to payoff using snowball or avalanche strategies."
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>Four steps. No surprises.</h2>
          </div>
          <div className="how">
            <Step title="Create your free account" body="Takes under a minute. No credit card, no phone number needed." />
            <Step title="Upload your credit report" body={<>Free from <a href="https://www.annualcreditreport.com" target="_blank" rel="noopener noreferrer" data-testid="landing-acr-link" style={{ color: '#B76E79', textDecoration: 'underline', fontWeight: 500 }}>AnnualCreditReport.com</a>. We analyze every tradeline for you.</>} />
            <Step title="Generate your letters" body="Pick what to dispute. We draft the FCRA letter for each bureau — you stay in control." />
            <Step title="Mail, track, respond" body="Mail from any post office. We count the 35 days and guide each next step." />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="alt">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Common questions</div>
            <h2>Is this really free?</h2>
            <p>Yes. We also answer a few more of the questions we hear most often.</p>
          </div>
          <div className="faq-grid">
            <Faq q="Is Selah Slate really free?">Yes — free forever. No credit card, no trial, no "premium" plan. It's a legacy-mission tool from Selah &amp; Stone Legacy Holdings LLC.</Faq>
            <Faq q="Are you a credit-repair company?">No. We're an educational tool. You stay in control of every letter you mail and every response you log. For legal advice, consult a licensed attorney.</Faq>
            <Faq q="Will Selah Slate guarantee my credit score goes up?">No honest tool can. Bureaus make their own decisions. What we <em>can</em> promise is clean, factual FCRA letters and smart follow-ups when a bureau says "verified".</Faq>
            <Faq q="What data do you collect?">Only what's needed to draft a letter: name, address, last 4 of SSN (encrypted), and the items you choose to dispute. PDF upload text is deleted within minutes of analysis. See our <Link to="/privacy">Privacy Policy</Link>.</Faq>
            <Faq q="Can I delete my account?">Yes — instantly, from Settings → Delete Account. All disputes, letters, journal entries, and testimonies tied to your account are permanently erased.</Faq>
            <Faq q="Why the faith element?">Because we believe financial freedom is more than a number. Scripture and practical action together produce lasting change. If that's not for you, the tool still works — the devotional content is optional.</Faq>
          </div>
        </div>
      </section>

      {/* BRAND FAMILY */}
      <section data-testid="brand-family-section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">More is coming</div>
            <h2>The Selah &amp; Stone Legacy Holdings Family</h2>
            <p>Selah Slate is the first of several legacy-mission tools we're building for families like yours. Watch this space.</p>
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}
            data-testid="brand-family-grid"
          >
            {[
              { name: 'Selah Slate', live: true },
              { name: 'Selah Downloads', live: false, sub: 'featuring Selah Rest™' },
              { name: 'Selah & Salt Home', live: false },
            ].map((b) => {
              const slug = b.name.toLowerCase().replace(/[^a-z]+/g, '-');
              return (
              <div
                key={b.name}
                data-testid={`brand-card-${slug}`}
                style={{
                  background: b.live ? '#36454F' : '#fff',
                  border: b.live ? '1px solid #36454F' : '1px solid rgba(54,69,79,0.12)',
                  borderRadius: 8,
                  padding: '30px 24px',
                  textAlign: 'center',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(54,69,79,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <h3 style={{ color: b.live ? '#E0BFB8' : undefined, marginBottom: b.sub ? 4 : 12 }}>{b.name}&trade;</h3>
                {b.sub && (
                  <p style={{ fontSize: '0.82rem', color: 'rgba(54,69,79,0.55)', margin: '0 0 12px', fontStyle: 'italic' }}>
                    {b.sub}
                  </p>
                )}
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    padding: '5px 14px',
                    borderRadius: 999,
                    background: b.live ? '#B76E79' : 'rgba(54,69,79,0.07)',
                    color: b.live ? '#fff' : 'rgba(54,69,79,0.55)',
                  }}
                >
                  {b.live ? 'Live now' : 'Coming soon'}
                </span>
                {!b.live && <WaitlistForm brand={b.name} slug={slug} />}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ paddingTop: 0 }}>
        <div className="cta">
          <h2>Your slate can be cleared.</h2>
          <p>Stop paying someone else to do what you can do for yourself. Free, private, faith-grounded — and always on your side.</p>
          <Link className="btn btn-primary" to="/register" data-testid="final-cta-register" style={{ background: '#E0BFB8', color: '#36454F' }}>
            Start clearing your slate →
          </Link>
          {(showApple || showAndroid) && (
            <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Or install the app on your device
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }} data-testid="final-cta-store-badges">
                {showApple && (
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                    data-testid="final-cta-apple-badge"
                    style={{ display: 'inline-block', transition: 'transform 0.18s ease, opacity 0.18s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.opacity = '0.92'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                  >
                    <img
                      src={APPLE_BADGE_SRC}
                      alt="Download on the App Store"
                      style={{ height: 60, width: 'auto', display: 'block' }}
                    />
                  </a>
                )}
                {showAndroid && (
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it on Google Play"
                    data-testid="final-cta-google-play-badge"
                    style={{ display: 'inline-block', transition: 'transform 0.18s ease, opacity 0.18s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.opacity = '0.92'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                  >
                    <img
                      src={PLAY_BADGE_SRC}
                      alt="Get it on Google Play"
                      style={{ height: 60, width: 'auto', display: 'block' }}
                    />
                  </a>
                )}
                {showWindows && (
                  <a
                    href={MS_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it from Microsoft Store"
                    data-testid="final-cta-microsoft-badge"
                    style={{ display: 'inline-block', transition: 'transform 0.18s ease, opacity 0.18s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.opacity = '0.92'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                  >
                    <img
                      src={MS_BADGE_SRC}
                      alt="Get it from Microsoft Store"
                      style={{ height: 60, width: 'auto', display: 'block' }}
                    />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="site">
        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="brand" style={{ color: '#fff', marginBottom: 14 }}>
                <ShieldLogo stroke="#E0BFB8" fill="rgba(224,191,184,0.08)" />
                <span className="name">Selah Slate&trade;</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.88rem', maxWidth: 360 }}>
                Part of Selah and Stone Legacy Holdings LLC. A legacy-mission tool provided free of charge. Not a credit-repair company, law firm, or financial advisor.
              </p>
            </div>
            <div>
              <h4>Product</h4>
              <Link to="/login">Open the app</Link>
              <Link to="/register">Create account</Link>
              <Link to="/resources" data-testid="landing-footer-resources-link">Free Credit Guides</Link>
              <Link to="/support">Help &amp; Support</Link>
            </div>
            <div>
              <h4>Legal</h4>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms &amp; Conditions</Link>
              <Link to="/disclosures">Disclosures</Link>
              <a href="mailto:support@selahslate.com">support@selahslate.com</a>
            </div>
          </div>
          <div className="legal" data-testid="landing-legal-notice">
            © {year} Selah &amp; Stone Legacy Holdings LLC · Hillsborough County, Florida
            <br />
            Selah Downloads&trade;, Selah Slate&trade;, Selah Rest&trade;, and Selah &amp; Salt Home&trade; are proprietary trademarks of Selah &amp; Stone Legacy Holdings LLC.
            <br />
            All rights reserved. Unauthorized reproduction or reverse engineering of this software codebase is strictly prohibited.
          </div>
        </div>
      </footer>
    </div>
  );
}
